import { useEffect, useState } from "react";

import {
  InfoPanel, Stepper,
  StepperProvider,
  useStepper,
  RadioGroup,
  RadioButton, TextField, Select, Checkbox, Modal,
  DocumentList, DocumentsModal, Button
} from "@king-ict/eupisi-ui/components";
import { CodeBook, UploadFile } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate, getYearFromDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  getCountryList
} from "../../api/Geography/GeographyApi";
import { getInstitutionById, searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import {
  getRequest, saveRequest, updateServiceData,
  getInitialQuestionnaire, getQuestionnaireExport,
  createOtherParentServiceData, setRequestStep,
  deleteRequest, getRequestDocument
} from "../../api/Request/RequestApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import { ContentArea, ToolbarTop, ToolbarEdit, FormCard, ToolbarStepper } from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  ValidationFailure, ServiceResultStatus, GenderEnum,
} from "../../models";
import { Institution, InstitutionCodeBookSearch } from "../../models/Institution";
import { DataTypeEnum, DisplayTypeEnum, Question, QuestionGroup, RequestAnswer } from "../../models/Questionnaire";
import {
  PersonDetails, Request, RequestStatus,
  RequestStep, RequestStepType, RequestType, DocumentType,
  PersonType, ContactMethod
} from "../../models/Request";
import ParentNumberEnum from "../../models/Request/ParentNumberEnum";
import ParentshipTypeEnum from "../../models/Request/ParentshipTypeEnum";
import { Attachment } from "../../models/Shared/Attachment";
import OtherParentDataCheckStatusEnum from "../../models/Shared/OtherParentDataCheckStatusEnum";
import { ClaimType } from "../../models/User";
import { 
  useGetAlternativeCodeReasonListQuery, useGetAlternativeCodeTypeListQuery, 
  useGetCitizenshipListQuery, useGetContactMethodListQuery, 
  useGetCountriesQuery, useGetGenderListQuery, useGetNationalityListQuery, 
  useGetNationalMinorityListQuery, useGetParentNumberListQuery, 
  useGetParentshipTypeListQuery, useGetProfessionalQualificationListQuery 
} from "../../queries/codeBookQuery";
import { requestListRoute } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { MsgUtils } from "../../utils";
import { InstitutionData } from "./RequestComponents/InstitutionData";

export function RequestEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const oibRegex = /^\d{11}$/;

  /**
   * This is the master list of steps as used by the API. 
   * Unfortunately, we don't have a way to "hide" steps so 
   * have to delete them from this list.
   */
  const steps = [
    { // Step 0 with substeps
      title: t("request.parentsStep"),
      substeps: [t("request.parentSubStepOne"), t("request.parentSubStepTwo")],
    },
    { // Step 1
      title: t("request.childStep"),
      substeps: [],
    },
    { // Step 2
      title: t("request.institutionStep"),
      substeps: [
        // t("request.institutionSubStep"),
        // t("request.locationAndProgramSubStep"),
      ],
    },
    { // Step 3
      title: t("request.initialConversationStep"),
      substeps: [], // dynamically populated
    },
    { // Step 4
      title: t("request.documentationStep"),
      substeps: [],
    },
    { // Step 5
      title: t("request.confirmStep"),
      substeps: [],
    },
  ];
  const referenceBirthday = new Date(1920, 11, 25);
  const defaultPersonDetails: PersonDetails = {
    id: 0,
    personId: 0,
    person: {},
    firstName: "",
    lastName: "",
    dateOfBirth: referenceBirthday,
    genderId: GenderEnum.Male,
  };
  const createDefault = (): Request => {
    return {
      id: 0,
      requestUID: "",
      code: "",
      statusId: RequestStatus.Draft,
      schoolYearId: 0,
      type: RequestType.RegularEnrollment,
      createdByAdmin: true,
      isOutsideEnrollmentArea: false,
      step: 0,
      subStep: 0,
    };
  };
  const { data: countries } = useGetCountriesQuery();
  const { data: genders } = useGetGenderListQuery();
  const { data: nationalMinorities } = useGetNationalMinorityListQuery();
  const { data: nationalities } = useGetNationalityListQuery();
  const { data: parentshipTypes } = useGetParentshipTypeListQuery();
  const { data: professionalQualifications } = useGetProfessionalQualificationListQuery();
  const { data: parentNumbers } = useGetParentNumberListQuery();
  const { data: contactMethods } = useGetContactMethodListQuery();
  const { data: alternativeCodeReasons } = useGetAlternativeCodeReasonListQuery();
  const { data: alternativeCodeTypes } = useGetAlternativeCodeTypeListQuery();
  const { data: citizenships } = useGetCitizenshipListQuery();

  const [item, setItem] = useState<Request>(createDefault());
  const [primaryInstitution, setPrimaryInstitution] = useState<Institution | undefined>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [parentTwoWithoutOib, setParentTwoWithoutOib] = useState<boolean>(false);
  const [showParentTwoOibAlert, setShowParentTwoOibAlert] = useState<string | undefined>();
  const [showParentTwoOibSuccess, setShowParentTwoOibSuccess] = useState<string | undefined>();

  const [secondaryInstitution, setSecondaryInstitution] = useState<Institution | undefined>();
  const [questionGroups, setQuestionGroups] = useState<QuestionGroup[]>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string | undefined>();
  const [actionMessage, setActionMessage] = useState<string | undefined>();
  /**
   * Gets the updated array of steps/substeps based on Request 
   * properties. Since there is no way hide steps or substeps, 
   * the only option is to delete the ones we won't use.
   * @returns The updated array of valid steps/substeps.
   */
  const getSteps = () => {
    let newSteps = [...steps];
    if (item.applicantParentDetails?.parentNumberId === ParentNumberEnum.OneParent) {
      newSteps[0].substeps.splice(1, 1);
    }
    if (questionGroups)
      newSteps[3].substeps = questionGroups.map(v => v.name);
    if (item.type === RequestType.EarlyEnrollment && !item.institutionProgramId) {
      newSteps.splice(2, 1);
    }
    if (item.type === RequestType.TemporaryExemption) {
      newSteps.splice(2, 2);
    }
    let otherSteps = [...newSteps];
    return otherSteps;
  };
  const [stepperState, stepperApi] = useStepper(getSteps());
  const [showUploadModal, setShowUploadModal] = useState<DocumentType | undefined>();
  const [uploadDocument, setUploadDocument] = useState<UploadFile[]>([]);
  const isEdited = useTypedSelector<boolean>((state) => state.application.isEdited);

  // not used.
  const getQuestionnaireDisplay = () => {
    const index = (item.subStep ?? 0);
    if (questionGroups && questionGroups.length > index) {
      const group = questionGroups[index];
      //setQuestionGroups(question.questionGroups);
      return (
        <>
          {/*             {questionGroups.map((v,i) => getQuestionnaireGroupDisplay(v, i))}
 */}
          {getQuestionnaireGroupDisplay(group, index)}
        </>
      );
    }
    return (<>No groups found.</>);
  };
  const getQuestionnaireGroupDisplay = (group: QuestionGroup, groupIndex: number) => {
    return (
      <FormCard
        cardTitle={group.name}
      >
        {group.questions?.map((v, i) => getQuestionnaireQuestionDisplay(v, groupIndex, i))}
      </FormCard>
    );
  };
  const getQuestionnaireQuestionDisplay = (question: Question, groupIndex: number, questionIndex: number) => {
    if (!question.requiredForAnswerId || item.questionnaireAnswers?.find(v => v.answerId === question.requiredForAnswerId) !== undefined) {
      switch (question.displayTypeId) {
        case DisplayTypeEnum.Date:
          return (
            <Row>
              <Col md={12}>DatePicker
                {/*             <DatePicker
              label={question.name}
              isClearable={!question.isRequired}
              value={item.questionnaireAnswers?.find(v => v.questionId === question.id)?.answerText !== undefined ? new Date(item.questionnaireAnswers!.find(v => v.questionId === question.id)!.answerText!) : new Date()}
              onChange={(e) => handleQuestionnaireQuestionChange(question.id, e.target.value)}
              required={question.isRequired}
            />
 */}            </Col>
            </Row>
          );
        case DisplayTypeEnum.Radio:
          return (
            <>
              <Row>
                <Col md={12}>
                  <RadioGroup
                    name={question.name}
                    title={question.name}
                    required={question.isRequired}
                    onChange={(e) => handleQuestionnaireQuestionChange(question.questionGroupQuestionId, undefined, Number.parseInt(e.target.value))}
                    errorText={errors?.find(e => e.propertyName.startsWith(`Questionnaire.QuestionGroups[${groupIndex}].Questions[${questionIndex}]`))?.message}
                  >
                    <Row>
                      {question.answers!.map(a => (
                        <Col md={4}>
                          <RadioButton
                            ariaLabel={a.name}
                            key={a.id}
                            label={a.name}
                            checked={item.questionnaireAnswers?.find(v => v.questionId === question.questionGroupQuestionId && v.answerId === a.id) !== undefined}
                            value={a.id}
                          />
                        </Col>
                      ))}
                    </Row>
                  </RadioGroup>
                </Col>
              </Row>
            </>
          );
        case DisplayTypeEnum.ReadOnly:
          return (
            <Row>
              <Col md={12}>
                <TextField
                  label={question.name}
                  required={question.isRequired}
                  value={item.questionnaireAnswers?.find(v => v.questionId === question.questionGroupQuestionId)?.answerText}
                  plaintext
                  errorText={errors?.find(e => e.propertyName.startsWith(`Questionnaire.QuestionGroups[${groupIndex}].Questions[${questionIndex}]`))?.message}
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.Select:
          return (
            <Row>
              <Col md={12}>
                <Select
                  label={question.name}
                  required={question.isRequired}
                  value={item.questionnaireAnswers?.find(v => v.questionId === question.questionGroupQuestionId)?.answerId}
                  onChange={(e) => handleQuestionnaireQuestionChange(question.questionGroupQuestionId, undefined, e.target.value)}
                  selectData={codeBookToSelectData(question.answers)}
                  errorText={errors?.find(e => e.propertyName.startsWith(`Questionnaire.QuestionGroups[${groupIndex}].Questions[${questionIndex}]`))?.message}
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.MultiSelect:
          return (
            <Row>
              <Col md={12}>
                <Select
                  multiple
                  label={question.name}
                  required={question.isRequired}
                  value={item.questionnaireAnswers?.filter(v => v.questionId === question.questionGroupQuestionId).map(v => v.answerId)}
                  onChange={(e) => handleQuestionnaireQuestionMultiChange(question.questionGroupQuestionId, e)}
                  selectData={codeBookToSelectData(question.answers)}
                  disabled={question.requiredForAnswerId !== undefined && item.questionnaireAnswers?.find(v => v.answerId === question.requiredForAnswerId) === undefined}
                  errorText={errors?.find(e => e.propertyName.startsWith(`Questionnaire.QuestionGroups[${groupIndex}].Questions[${questionIndex}]`))?.message}
                />
              </Col>
            </Row>
          );
        case DisplayTypeEnum.TextArea:
          return (
            <Row>
              <Col md={12}>TextEditor
                {/*             <TextEditor
              label={question.name} 
              initialValue={item.questionnaireAnswers?.find(v => v.questionId === question.id)?.answerText}
              handleChange={(s) => handleQuestionnaireQuestionChange(question.id, s)}
              required={question.isRequired}
            />
 */}            </Col>
            </Row>
          );
        case DisplayTypeEnum.Time:
          return (
            <Row>
              <Col md={12}>Time

              </Col>
            </Row>
          );
        case DisplayTypeEnum.Upload:
          return (
            <Row>
              <Col md={12}>Upload

              </Col>
            </Row>
          );
        default: // Text
          return (
            <Row>
              <Col md={12}>
                <TextField
                  type={question.dataTypeId === DataTypeEnum.Integer ? "number" : "text"}
                  label={question.name}
                  onChange={(e) => handleQuestionnaireQuestionChange(question.questionGroupQuestionId, e.target.value)}
                  value={item.questionnaireAnswers?.find(v => v.questionId === question.id)?.answerText}
                  maxLength={question.answerLength}
                  required={question.isRequired}
                  disabled={question.requiredForAnswerId !== undefined && item.questionnaireAnswers?.find(v => v.answerId === question.requiredForAnswerId) === undefined}
                  errorText={errors?.find(e => e.propertyName.startsWith(`Questionnaire.QuestionGroups[${groupIndex}].Questions[${questionIndex}]`))?.message}
                />
              </Col>
            </Row>
          );
      }
    }
  };
  const getParentTwoBirthYears = (): CodeBook[] => {
    let arr = [] as CodeBook[];
    let year = getYearFromDate(new Date);
    for (var i = (year - 100); i <= (year - 12); i++) {
      arr.push({ id: i, name: i.toString() });
    }
    return arr;
  };
  const filterRequiredAnswers = (answers: RequestAnswer[]): RequestAnswer[] => {
    let questionArr = questionGroups.flatMap(qg => qg.questions?.filter(q => q.requiredForAnswerId != null && answers.find(a => a.answerId === q.requiredForAnswerId) === undefined));
    let answerArr = answers.filter(a => questionArr.find(q => q?.questionGroupQuestionId === a.questionId) === undefined);
    return answerArr;
  };

  useEffect(() => {
    (async () => {
      var questionnairePromise = getInitialQuestionnaire();

      var result = (await getRequest(+id));
      if (result) {
        setItem(() => result);
        setParentTwoWithoutOib(result.otherParentDetails?.person.alternativeDocumentNumber !== undefined);
      }
      var questionnaire = await questionnairePromise;
      if (questionnaire && questionnaire.questionGroups) {
        setQuestionGroups(() => questionnaire.questionGroups);
      }
    })();
    dispatch(resetIsEdited())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const stepAdjust = item.type === RequestType.TemporaryExemption
        ? 2
        : (item.type === RequestType.EarlyEnrollment && !item.institutionProgramId
          ? 1
          : 0);
      // The API sets the step/substep according to the master step 
      // list but some of these may have been deleted for the stepper. 
      // We need to map the master list to the stepper list.
      switch (item.step) {
        case RequestStep.ParentData:
          if ((item.subStep ?? 0) === 0) {
            stepperApi.moveToSpecific(0, 0);
          } else {
            stepperApi.moveToSpecific(0, 1);
          }
          break;
        case RequestStep.ChildData:
          stepperApi.moveToSpecific(1, 0);
          break;
        case RequestStep.InstitutionProgramData:
          //await ensurePrimaryInstitution();
          stepperApi.moveToSpecific(2, 0);
          break;
        case RequestStep.InitialQuestionnaireData:
          stepperApi.moveToSpecific(3 - stepAdjust, (item.subStep ?? 0));
          break;
        case RequestStep.DocumentationData:
          stepperApi.moveToSpecific(4 - stepAdjust, 0);
          break;
        case RequestStep.Approval:
          stepperApi.moveToSpecific(5 - stepAdjust, 0);
          break;
        default:
          stepperApi.moveToSpecific(0, 0);
          break;
      };
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.step, item.subStep, item.type]);
  useEffect(() => {
    (async () => {
      if (item.institutionId && item.schoolYearId > 0 && (!primaryInstitution || primaryInstitution.id !== item.institutionId)) {
        const inst = await getInstitutionById(item.institutionId, item.schoolYearId, true);
        if (inst)
          setPrimaryInstitution(inst);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.institutionId, item.schoolYearId]);
  useEffect(() => {
    (async () => {
      if (item.outsideEnrollmentAreaInstitutionProgram && item.schoolYearId && (!secondaryInstitution || secondaryInstitution.id !== item.outsideEnrollmentAreaInstitutionProgram.institutionId))
        setSecondaryInstitution((await getInstitutionById(item.outsideEnrollmentAreaInstitutionProgram.institutionId, item.schoolYearId, true)));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.outsideEnrollmentAreaInstitutionProgram]);
  // useEffect(() => {
  //   (async () => {
  //     if (auth.currentRole?.claimType === ClaimType.Institution && auth.currentRole?.locations) {
  //       if (auth.currentRole?.locations && item.id !== 0) {
  //         const filter: InstitutionCodeBookSearch = {
  //           ids: auth.currentRole.locations,
  //           includeOutsideEnrollmentArea: true,
  //         };
  //         const result = await searchInstitutionNew(filter);
  //         if (result) {
  //           if (result.length > 0 && (item.institutionId === undefined || result.find(v => v.id === item.institutionId) === undefined))
  //             handlePrimaryInstitutionChange(result[0].id)
  //         }
  //       }
  //     }
  //   })();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [auth.currentRole?.locations, item.id]);

  const handlePrimaryInstitutionChange = (val: number) => {
    const institutionId = item.institutionId;
    const r: Request = {
      ...item,
      institutionId: val,
      institutionProgramId: undefined,
      electiveSubjects: item.electiveSubjects?.filter(v => v.parentId !== institutionId) ?? [],
      mandatoryForeignLanguages: item.mandatoryForeignLanguages?.filter(v => v.parentId !== institutionId) ?? [],
      foreignLanguages: item.foreignLanguages?.filter(v => v.parentId !== institutionId) ?? [],
      languageSchoolingModels: item.languageSchoolingModels?.filter(v => v.parentId !== institutionId) ?? [],
    };
    handleUpdate(r);
  };
  const handleParentOneNationalMinorityChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      nationalMinorityId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneNationalityChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      nationalityId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneParentshipTypeChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      parentshipTypeId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneProfessionalQualificationChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      professionalQualificationId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneProfessionNameChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      professionName: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOnePhoneNumberChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      phoneNumber: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneEmailChange = (e: any) => {
    const val = e.target.value as string;
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      email: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneParentNumberChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      parentNumberId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentOneContactMethodChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.applicantParentDetails!,
      contactMethodId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      applicantParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoWithoutOibChange = () => {
    const newVal = !parentTwoWithoutOib;    
    setParentTwoWithoutOib(newVal);
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      dateOfBirth: newVal ? pd.dateOfBirth : defaultPersonDetails.dateOfBirth,
      person: {
        ...pd.person,
        alternativeDocumentNumber: newVal ? pd.person.alternativeDocumentNumber : undefined,
        alternativeDocumentReasonId: newVal ? pd.person.alternativeDocumentReasonId : undefined,
        alternativeDocumentTypeId: newVal ? pd.person.alternativeDocumentTypeId : undefined,
        oib: newVal ? undefined : pd.person.oib,
      },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoDocumentNumberChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      person: { ...pd.person, alternativeDocumentNumber: val && val.length > 0 ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoDocumentTypeChange = (val: number) => {
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      person: { ...pd.person, alternativeDocumentTypeId: !isNaN(val) ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoDocumentReasonChange = (val: number) => {
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      person: { ...pd.person, alternativeDocumentReasonId: !isNaN(val) ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoLastNameChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      lastName: val && val.length > 0 ? val : "",
      person: { ...pd.person, lastName: val && val.length > 0 ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoFirstNameChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      firstName: val && val.length > 0 ? val : "",
      person: { ...pd.person, firstName: val && val.length > 0 ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoOibChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      person: { ...pd.person, oib: val && val.length > 0 ? val : undefined },
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoOibCheckClick = async () => {
    if (item.otherParentDetails?.person.oib && oibRegex.test(item.otherParentDetails.person.oib)) {
      const result = await createOtherParentServiceData(item.id, item.otherParentDetails);
      if (result 
        && (result.status & OtherParentDataCheckStatusEnum.Success
          ) === OtherParentDataCheckStatusEnum.Success
        && result.item) {
        handleUpdate(result.item);
        setShowParentTwoOibSuccess(t(MsgUtils.getOtherParentValidationMessage(result.status)));
        setShowParentTwoOibAlert(undefined);
      } else {
        setShowParentTwoOibSuccess(undefined);
        setShowParentTwoOibAlert(t(MsgUtils.getOtherParentValidationMessage(result.status)));
      }
    }
  };
  const handleParentTwoUploadDocumentClick = () => {
    setUploadDocument(item.otherUploadFiles ?? []);
    setShowUploadModal(DocumentType.OtherParent);
  };
  const handleParentTwoYearOfBirthChange = (e: any) => {
    //const val = e.target.value as string;
    var year = Number.parseInt(e.target.value);
    //const yearRegex = /^\d{1,4}$/;
    //if (isNaN(year) || !yearRegex.test(val)) {
    //  year = getYearFromDate(referenceBirthday);
    //}
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const dateOfBirth = new Date(pd.dateOfBirth);
    dateOfBirth.setUTCFullYear(year);
    const p: PersonDetails = {
      ...pd,
      dateOfBirth: dateOfBirth,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoProfessionalQualificationChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      professionalQualificationId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoProfessionChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      professionName: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoParentshipTypeChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      parentshipTypeId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoPhoneNumberChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      phoneNumber: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoEmailChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      email: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleParentTwoCustodyChange = (e: any) => {
    const val = e.target.value as string;
    const pd = item.otherParentDetails ?? defaultPersonDetails;
    const p: PersonDetails = {
      ...pd,
      custody: val && val.length > 0 ? val : undefined,
    };
    const r: Request = {
      ...item,
      otherParentDetails: p,
    };
    handleUpdate(r);
  };
  const handleChildNationalMinorityChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.childDetails!,
      nationalMinorityId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      childDetails: p,
    };
    handleUpdate(r);
  };
  const handleChildNationalityChange = (e: any) => {
    const val = Number.parseInt(e.target.value);
    const p: PersonDetails = {
      ...item.childDetails!,
      nationalityId: !isNaN(val) ? val : undefined,
    };
    const r: Request = {
      ...item,
      childDetails: p,
    };
    handleUpdate(r);
  };
  const handleQuestionnaireQuestionChange = (id: number, val?: string, answerId?: number) => {
    const requestAnswer: RequestAnswer = item.questionnaireAnswers?.find(v => v.questionId === id) ?? { questionId: id };
    const newRequestAnswer = {
      ...requestAnswer,
      answerId: answerId,
      answerText: val,
    };
    var arr = item.questionnaireAnswers?.filter(v => v.questionId !== id) ?? [];
    arr.push(newRequestAnswer);
    arr = filterRequiredAnswers(arr);
    const r: Request = {
      ...item,
      questionnaireAnswers: arr,
    };
    handleUpdate(r);
  };
  const handleQuestionnaireQuestionMultiChange = (id: number, e: any) => {
    const valList = e.target.value as number[];
    var arr = item.questionnaireAnswers?.filter(v => v.questionId !== id) ?? [];
    valList.forEach(v => {
      const newRequestAnswer: RequestAnswer = {
        questionId: id,
        answerId: v,
        answerText: undefined,
      };
      arr.push(newRequestAnswer);
    });
    arr = filterRequiredAnswers(arr);
    const r: Request = {
      ...item,
      questionnaireAnswers: arr,
    };
    handleUpdate(r);
  };
  const handleChildUploadDocumentClick = () => {
    setUploadDocument(() => item.childUploadFiles ?? []);
    setShowUploadModal(DocumentType.Child);
  };
  const handlePsychophysicalConditionDecisionUploadDocumentClick = () => {
    setUploadDocument(() => item.psychophysicalConditionDecisionUploadFiles ?? []);
    setShowUploadModal(DocumentType.PsychophysicalConditionDecision);
  };
  const handleUniqueBodyOfExpertiseUploadDocumentClick = () => {
    setUploadDocument(() => item.uniqueBodyOfExpertiseUploadFiles ?? []);
    setShowUploadModal(DocumentType.UniqueBodyOfExpertise);
  };
  const handleMedicalUploadDocumentClick = () => {
    setUploadDocument(() => item.medicalUploadFiles ?? []);
    setShowUploadModal(DocumentType.Medical);
  };
  const handleParentUploadDocumentClick = () => {
    setUploadDocument(() => item.parentUploadFiles ?? []);
    setShowUploadModal(DocumentType.Parent);
  };
  const handlePsychologyUploadDocumentClick = () => {
    setUploadDocument(() => item.psychologyUploadFiles ?? []);
    setShowUploadModal(DocumentType.Psychology);
  };
  const handleEducationUploadDocumentClick = () => {
    setUploadDocument(() => item.educationalRehabilitationUploadFiles ?? []);
    setShowUploadModal(DocumentType.EducationalRehabilitation);
  };
  const handleOtherUploadDocumentClick = () => {
    setUploadDocument(() => item.otherUploadFiles ?? []);
    setShowUploadModal(DocumentType.OtherParent);
  };
  /**
   * Reads the file binary data and pushes the listed files to the uploadDocument state variable.
   * @param files 
   */
  const handleFileChange = async (files: File[]) => {
    var uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach(file => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        var uploadedFile = { fileName: file.name, fileData: fileReader.result } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    setUploadDocument(uploadedFiles.length > 0 ? uploadedFiles : []);
  };
  /**
   * Pushes the listed files to the uploadDocument state variable, sets
   * the appropriate collection on the item, and closes the modal.
   * @param files 
   */
  const handleUploadFileChange = (files: UploadFile[]) => {
    const newItem: Request = {
      ...item,
    };
    const combined = files.concat(uploadDocument);
    setUploadDocument(combined ?? []);
    //handleUpdate(newItem);
    switch (showUploadModal) {
      case DocumentType.Parent:
        newItem.parentUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.OtherParent:
        newItem.otherUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.Child:
        newItem.childUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.Medical:
        newItem.medicalUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.EducationalRehabilitation:
        newItem.educationalRehabilitationUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.Psychology:
        newItem.psychologyUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.PsychophysicalConditionDecision:
        newItem.psychophysicalConditionDecisionUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
      case DocumentType.UniqueBodyOfExpertise:
        newItem.uniqueBodyOfExpertiseUploadFiles = combined && combined.length > 0 ? combined : undefined;
        break;
    }
    setShowUploadModal(undefined);
    handleUpdate(newItem);
  };
  /**
   * Removes the specified doc (existing attachment) from the specified item collection.
   * @param doc 
   */
  const handleRemoveAttachment = (doc: Attachment, type: DocumentType) => {
    if (doc && doc.id) {
      const newItem: Request = {
        ...item,
      };

      switch (type) {
        case DocumentType.Parent:
          const parentAttachments = item.parentAttachments?.filter(v => v.id !== doc.id);
          newItem.parentAttachments = parentAttachments && parentAttachments.length > 0 ? parentAttachments : undefined;
          break;
        case DocumentType.OtherParent:
          const otherAttachments = item.otherAttachments?.filter(v => v.id !== doc.id);
          newItem.otherAttachments = otherAttachments && otherAttachments.length > 0 ? otherAttachments : undefined;
          break;
        case DocumentType.Child:
          const childAttachments = item.childAttachments?.filter(v => v.id !== doc.id);
          newItem.childAttachments = childAttachments && childAttachments.length > 0 ? childAttachments : undefined;
          break;
        case DocumentType.Medical:
          const medicalAttachments = item.medicalAttachments?.filter(v => v.id !== doc.id);
          newItem.medicalAttachments = medicalAttachments && medicalAttachments.length > 0 ? medicalAttachments : undefined;
          break;
        case DocumentType.EducationalRehabilitation:
          const educationalRehabilitationAttachments = item.educationalRehabilitationAttachments?.filter(v => v.id !== doc.id);
          newItem.educationalRehabilitationAttachments = educationalRehabilitationAttachments && educationalRehabilitationAttachments.length > 0 ? educationalRehabilitationAttachments : undefined;
          break;
        case DocumentType.Psychology:
          const psychologyAttachments = item.psychologyAttachments?.filter(v => v.id !== doc.id);
          newItem.psychologyAttachments = psychologyAttachments && psychologyAttachments.length > 0 ? psychologyAttachments : undefined;
          break;
        case DocumentType.PsychophysicalConditionDecision:
          const psychophysicalConditionDecisionAttachments = item.psychophysicalConditionDecisionAttachments?.filter(v => v.id !== doc.id);
          newItem.psychophysicalConditionDecisionAttachments = psychophysicalConditionDecisionAttachments && psychophysicalConditionDecisionAttachments.length > 0 ? psychophysicalConditionDecisionAttachments : undefined;
          break;
        case DocumentType.UniqueBodyOfExpertise:
          const uniqueBodyOfExpertiseAttachments = item.uniqueBodyOfExpertiseAttachments?.filter(v => v.id !== doc.id);
          newItem.uniqueBodyOfExpertiseAttachments = uniqueBodyOfExpertiseAttachments && uniqueBodyOfExpertiseAttachments.length > 0 ? uniqueBodyOfExpertiseAttachments : undefined;
          break;
      }
      handleUpdate(newItem);
    }
  };
  const handleDocumentDownload = async (doc: Attachment, type: DocumentType) => {
    if (doc && doc.id) {
      await getRequestDocument(doc.id, item.id, type);
    }
  };
  const handleUpdateServiceData = async (personType: PersonType) => {
    const result = await updateServiceData(item.id, personType);
    if (result) {
      setErrors(result.validationFailures);
      if (result.result)
        setItem(result.result);
      if (result.isValid && personType === PersonType.OtherParent)
        setShowParentTwoOibSuccess(t(MsgUtils.getOtherParentValidationMessage(OtherParentDataCheckStatusEnum.Success)));
    }
  };
  const handleGetQuestionnaireClicked = async () => {
    await getQuestionnaireExport(item.id);
  };
  const handleUpdate = (request: Request) => {
    setItem(request);
    dispatch(setIsEdited());
  };
  const handleDeleteClicked = async () => {
    setIsDeleting(true);
    if ((item.type === RequestType.EarlyEnrollment) || (item.type === RequestType.TemporaryExemption))
      setConfirmMessage(t("request.cancelConfirmation"));
    else
      setConfirmMessage(t("request.deleteConfirmation"));
  };
  const handleSaveClicked = async (stepType: RequestStepType, confirmed?: boolean) => {
    if (stepType !== RequestStepType.NextStep || item.step !== RequestStep.Approval) {
      doSave(stepType);
    } else {
      setIsDeleting(false);
      setConfirmMessage(t("request.createConfirmation"));
    }
  };
  const handleConfirmClicked = async () => {
    setConfirmMessage(undefined);
    if (!isDeleting) {
      await doSave(RequestStepType.NextStep);
    } else {
      await doDelete();
    }
  };
  const handleRedirect = () => {
    history.push(requestListRoute);
  };
  const handleStepChange = async (step: RequestStep) => {
    var result = await setRequestStep(item, step);
    if (result) {
      setErrors(result.validationFailures);
      if (result.result) {
        setItem(result.result);
        dispatch(resetIsEdited());
        setShowParentTwoOibAlert(undefined);
        setShowParentTwoOibSuccess(undefined);
      }
    }
  }
  const doDelete = async () => {
    var result = await deleteRequest(item.id);
    if (result) {
      setErrors(result.validationFailures);
      if (result.isValid) {
        if ((item.type === RequestType.EarlyEnrollment) || (item.type === RequestType.TemporaryExemption))
          setActionMessage(t("request.requestCanceled"));
        else
          setActionMessage(t("request.requestDeleted"));
      }
    }
  };
  const doSave = async (stepType: RequestStepType) => {
    var result = await saveRequest(item, stepType);
    if (result) {
      setErrors(result.validationFailures);
      if (result.result) {
        setItem(result.result);
        dispatch(resetIsEdited());
        setShowParentTwoOibAlert(undefined);
        setShowParentTwoOibSuccess(undefined);

        if (result.isValid) {
          if (result.result.id > 0)
            setActionMessage(t("request.requestCreated"));
          else if (stepType === RequestStepType.SaveOnly)
            setActionMessage(t("request.requestSaved"));
        }
      }
    }
  };
  const getPageTitle = (): string => {
    var typeString;
    switch (item.type) {
      case RequestType.EarlyEnrollment:
        typeString = t("request.requestTitleEarlyEnrollment");
        break;
      case RequestType.RegularEnrollment:
        typeString = t("request.requestTitleRegularEnrollment");
        break;
      case RequestType.RegularEnrollmentForChildrenWithLearningDifficulties:
        typeString = t("request.requestTitleRegularEnrollmentForChildrenWithLearningDifficulties");
        break;
      case RequestType.TemporaryExemption:
        typeString = t("request.requestTitleTemporaryExemption");
        break;
    }
    typeString += item.schoolYearCode ? item.schoolYearCode! : "";
    return typeString;
  };

  return (
    <>
      <StepperProvider>
        <Row>
          <Col md={3}>
            <aside>
              <Stepper data={getSteps()} {...stepperState} />
            </aside>
          </Col>
          <Col md={9}>
            <ContentArea>
              <ToolbarTop
                showAdd={"hide"}
                showBack={"hide"}
                showEdit={"hide"}
                pageTitle={getPageTitle()}
                errors={errors}
              />
              {(item.step === RequestStep.ParentData && (item.subStep ?? 0) === 0) &&
                <FormCard
                  cardTitle={t("request.parentSubStepOne")}
                >
                  <Row>
                    <Col md={12}>
                      <InfoPanel
                        message={t("request.mainInfoMessage")}
                        color="warning"
                        fullWidth
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                      {/*                         <Select
                            required
                            label={t("common.schoolCandidateModel.institutionList")}
                            name={"institutionId"}
                            value={item?.institutionId}
                            onChange={(e) => handlePrimaryInstitutionChange(Number.parseInt(e.target.value))}
                            selectData={codeBookToSelectData(institutionList)}
                            disabled={institutionList?.length === 1 || item.id !== 0}
                            errorText={errors?.find(e => e.propertyName === "InstitutionId")?.message}
                          />
 */}                      </Col>
                    <Col md={4}>
                      <Button
                        color="primary"
                        disabled={!(item.applicantParentDetails?.person?.oib !== undefined && oibRegex.test(item.applicantParentDetails.person.oib))}
                        outline
                        onClick={() => handleUpdateServiceData(PersonType.Parent)}
                        size="sm"
                      >
                        {t("common.schoolCandidateModel.refreshOibData")}
                      </Button>
                    </Col>
                  </Row>
                  <FormCard
                    cardTitle={t("request.basicInformation")}
                  >
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={t("person.firstName")}
                          helperText={t("common.birthRegistry")}
                          value={item?.applicantParentDetails?.firstName}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={t("person.lastName")}
                          helperText={t("common.birthRegistry")}
                          value={item?.applicantParentDetails?.lastName}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.oib")}
                          helperText={t("common.oibApi")}
                          value={item?.applicantParentDetails?.person.oib}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.dateOfBirth")}
                          helperText={t("common.birthRegistry")}
                          value={item?.applicantParentDetails
                            ? hrDate(item?.applicantParentDetails.dateOfBirth)
                            : ""
                          }
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.sex")}
                          helperText={t("common.birthRegistry")}
                          value={genders?.find(v => v.id === item?.applicantParentDetails?.genderId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.birthplace")}
                          helperText={t("common.birthRegistry")}
                          value={item?.applicantParentDetails?.cityOfBirth}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.countryBirth")}
                          helperText={t("common.birthRegistry")}
                          value={countries?.find(v => v.id === item?.applicantParentDetails?.countryOfBirthId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.citizenship")}
                          helperText={t("common.birthRegistry")}
                          value={citizenships?.find(v => v.id === item?.applicantParentDetails?.citizenshipId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          emptyOption={{ value: undefined, text: "" }}
                          label={t("person.nationalMinority")}
                          name={"nationalMinority"}
                          value={item?.applicantParentDetails?.nationalMinorityId}
                          onChange={handleParentOneNationalMinorityChange}
                          selectData={codeBookToSelectData(nationalMinorities)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.NationalMinorityId")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          required
                          label={t("person.nationality")}
                          name={"nationality"}
                          value={item?.applicantParentDetails?.nationalityId}
                          onChange={handleParentOneNationalityChange}
                          selectData={codeBookToSelectData(nationalities)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.NationalityId")?.message}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <Select
                          required
                          label={t("person.parentshipType")}
                          name={"parentshipTypeId"}
                          value={item?.applicantParentDetails?.parentshipTypeId}
                          onChange={handleParentOneParentshipTypeChange}
                          selectData={codeBookToSelectData(parentshipTypes)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.ParentshipTypeId")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          label={t("person.professionalQualification")}
                          name={"parentshipTypeId"}
                          value={item?.applicantParentDetails?.professionalQualificationId}
                          onChange={handleParentOneProfessionalQualificationChange}
                          selectData={codeBookToSelectData(professionalQualifications)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.ProfessionalQualificationId")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          required
                          type="text"
                          label={t("person.profession")}
                          helperText={t("common.profession")}
                          value={item?.applicantParentDetails?.professionName}
                          onChange={handleParentOneProfessionNameChange}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.ProfessionName")?.message}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.phoneNumber")}
                          name={"phoneNumber"}
                          value={item?.applicantParentDetails?.phoneNumber}
                          onChange={handleParentOnePhoneNumberChange}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.PhoneNumber")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          required={item.applicantParentDetails?.contactMethodId === ContactMethod.Email}
                          type="text"
                          label={t("person.email")}
                          name={"email"}
                          value={item?.applicantParentDetails?.email}
                          onChange={handleParentOneEmailChange}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.Email")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          required
                          label={t("person.parentNumber")}
                          name={"parentNumber"}
                          value={item?.applicantParentDetails?.parentNumberId}
                          onChange={handleParentOneParentNumberChange}
                          selectData={codeBookToSelectData(parentNumbers)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.ParentNumberId")?.message}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Select
                          required
                          label={t("person.contactMethod")}
                          name={"contactMethod"}
                          value={item?.applicantParentDetails?.contactMethodId}
                          onChange={handleParentOneContactMethodChange}
                          selectData={codeBookToSelectData(contactMethods)}
                          errorText={errors?.find(e => e.propertyName === "ApplicantParentDetails.ContactMethodId")?.message}
                        />
                      </Col>
                    </Row>
                  </FormCard>
                  <FormCard
                    cardTitle={t("person.permanentResidenceInformation")}
                  >
                    {/*       <Row className={styles.rowHeadingSpace}>
        <Col md={12}>
          {applicantParentState.integrationResultStatus &&
            applicantParentState.integrationResultStatus ===
              IntegrationResultStatusEnum.OibFailed && (
              <InfoPanel
                message={t("person.parentPermanentResidenceError")}
                color="danger"
                className={styles.errorMessage}
              />
            )}
        </Col>
      </Row>
 */}
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.country")}`}
                          value={countries?.find(v => v.id === item?.applicantParentDetails?.residence?.countryId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.municipality"
                          )}`}
                          value={item?.applicantParentDetails?.residence?.permanentAddress?.municipality ?? item?.applicantParentDetails?.residence?.address}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.town")}`}
                          value={item?.applicantParentDetails?.residence?.permanentAddress?.town}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.postalCode"
                          )}`}
                          value={item?.applicantParentDetails?.residence?.permanentAddress?.postalCode}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.street")}`}
                          value={item?.applicantParentDetails?.residence?.permanentAddress?.street}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.streetNumber"
                          )}`}
                          value={item?.applicantParentDetails?.residence?.permanentAddress?.houseNumber}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                  </FormCard>
                  {item?.applicantParentDetails?.residence?.temporaryAddress && (
                    <FormCard
                      cardTitle={t("person.temporaryResidenceInformation")}
                    >
                      <Row>
                        <Col md={4}>
                          {/*<TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.country"
                            )}`}
                            value={countries?.find(v => v.id === item?.applicantParentDetails?.residence?.countryId)?.name}
                            plaintext
                            bold
                            />*/}
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.municipality"
                            )}`}
                            value={item?.applicantParentDetails?.residence?.temporaryAddress?.municipality}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.town"
                            )}`}
                            value={item?.applicantParentDetails?.residence?.temporaryAddress?.town}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.postalCode"
                            )}`}
                            value={item?.applicantParentDetails?.residence?.temporaryAddress?.postalCode}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.street"
                            )}`}
                            value={item?.applicantParentDetails?.residence?.temporaryAddress?.street}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumber"
                            )}`}
                            value={item?.applicantParentDetails?.residence?.temporaryAddress?.houseNumber}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                    </FormCard>
                  )}
                </FormCard>
              }
              {(item.step === RequestStep.ParentData && (item.subStep ?? 0) === 1) &&
                <FormCard
                  cardTitle={t("request.parentSubStepTwo")}
                >
                  <Row>
                    <Col md={12}>
                      <InfoPanel
                        message={t("request.mainInfoMessageOtherParent")}
                        color="warning"
                        fullWidth
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                    </Col>
                    <Col md={4}>
                      <Button
                        color="primary"
                        disabled={!(item.otherParentDetails?.person?.oib !== undefined 
                          && oibRegex.test(item.otherParentDetails.person.oib) && !isEdited)}
                        outline
                        onClick={() => handleUpdateServiceData(PersonType.OtherParent)}
                        size="sm"
                      >
                        {t("common.schoolCandidateModel.refreshOibData")}
                      </Button>
                    </Col>
                  </Row>
                  <FormCard
                    cardTitle={t("request.basicInformation")}
                  >
                    <Row>
                      <Col md={4}>
                        <Checkbox
                          label={t("common.requestModel.parent2WithoutOib")}
                          name={"doesntHaveOIB"}
                          checked={parentTwoWithoutOib}
                          onChange={handleParentTwoWithoutOibChange}
                        />
                      </Col>
                    </Row>
                    {(parentTwoWithoutOib) &&
                      <Row>
                        <Col md={4}>
                          <TextField
                            required
                            label={t("common.schoolCandidateModel.alternativeCode")}
                            value={item.otherParentDetails?.person.alternativeDocumentNumber}
                            onChange={handleParentTwoDocumentNumberChange}
                            errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Person.AlternativeDocumentNumber")?.message}
                          />
                        </Col>
                        <Col md={4}>
                          <Select
                            required
                            label={t("common.schoolCandidateModel.schoolCandidateAlternativeCodeType")}
                            name={"schoolCandidate.schoolCandidateAlternativeCodeType"}
                            value={item.otherParentDetails?.person.alternativeDocumentTypeId}
                            onChange={(e) => handleParentTwoDocumentTypeChange(Number.parseInt(e.target.value))}
                            selectData={codeBookToSelectData(alternativeCodeTypes)}
                            emptyOption={{ value: undefined, text: undefined }}
                            errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Person.AlternativeDocumentTypeId")?.message}
                          />
                        </Col>
                        <Col md={4}>
                          <Select
                            required
                            label={t("common.schoolCandidateModel.schoolCandidateAlternativeCodeReason")}
                            name={"schoolCandidate.schoolCandidateAlternativeCodeReason"}
                            value={item.otherParentDetails?.person.alternativeDocumentReasonId}
                            onChange={(e) => handleParentTwoDocumentReasonChange(Number.parseInt(e.target.value))}
                            selectData={codeBookToSelectData(alternativeCodeReasons)}
                            emptyOption={{ value: undefined, text: undefined }}
                            errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Person.AlternativeDocumentReasonId")?.message}
                          />
                        </Col>
                      </Row>
                    }
                    <Row>
                      <Col md={4}>
                        <TextField
                          required
                          label={t("common.requestModel.parentLastName")}
                          name={"item.parent.lastName"}
                          value={item.otherParentDetails?.lastName}
                          onChange={handleParentTwoLastNameChange}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.LastName")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          required
                          label={t("common.requestModel.parentFirstName")}
                          name={"item.parent.firstName"}
                          value={item.otherParentDetails?.firstName}
                          onChange={handleParentTwoFirstNameChange}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.FirstName")?.message}
                        />
                      </Col>
                      {(!parentTwoWithoutOib) &&
                        <Col md={4}>
                          <TextField
                            label={t("common.requestModel.parentOib")}
                            name={"item.parent.oib"}
                            value={item.otherParentDetails?.person.oib}
                            onChange={handleParentTwoOibChange}
                            errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Person.OIB")?.message}
                          />
                        </Col>
                      }
                    </Row>
                    {(!parentTwoWithoutOib) &&
                      <Row>
                        <Col md={4}>
                          <Button
                            color="primary"
                            disabled={!(item.otherParentDetails?.person?.oib !== undefined 
                              && oibRegex.test(item.otherParentDetails.person.oib) && isEdited)}
                            onClick={handleParentTwoOibCheckClick}
                            size="sm"
                          >
                            {t("common.requestModel.getOibServiceData")}
                          </Button>
                        </Col>
                        <Col md={5}>
                          {!showParentTwoOibAlert || <InfoPanel color="danger" message="" dismissible={false} fullWidth={true} >{showParentTwoOibAlert}</InfoPanel>}
                          {!showParentTwoOibSuccess || <InfoPanel color="success" message="" dismissible={false} fullWidth={true} >{showParentTwoOibSuccess}</InfoPanel>}
                        </Col>
                      </Row>
                    }
                    {(parentTwoWithoutOib) &&
                      <>
                        <Row>
                          <Col md={12}>
                            <InfoPanel
                              message={t("request.otherParentDocument")}
                              color="warning"
                              fullWidth
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.otherAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.OtherParent)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.OtherParent)}
                              removable={true}
                            />
                          </Col>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleParentTwoUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.otherUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                        </Row>
                      </>
                    }
                    <Row>
                      <Col md={4}>
                        <Select
                          required
                          label={t("person.yearOfBirth")}
                          name={"item.parent.oib"}
                          value={item.otherParentDetails ? getYearFromDate(item.otherParentDetails?.dateOfBirth) : undefined}
                          selectData={codeBookToSelectData(getParentTwoBirthYears())}
                          onChange={handleParentTwoYearOfBirthChange}
                          disabled={!parentTwoWithoutOib}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.DateOfBirth")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          label={t("person.professionalQualification")}
                          name={"parentshipTypeId"}
                          value={item?.otherParentDetails?.professionalQualificationId}
                          onChange={handleParentTwoProfessionalQualificationChange}
                          selectData={codeBookToSelectData(professionalQualifications)}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.ProfessionalQualificationId")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          required
                          type="text"
                          label={t("person.profession")}
                          helperText={t("common.profession")}
                          value={item?.otherParentDetails?.professionName}
                          onChange={handleParentTwoProfessionChange}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.ProfessionName")?.message}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.phoneNumber")}
                          name={"phoneNumber"}
                          value={item?.otherParentDetails?.phoneNumber}
                          onChange={handleParentTwoPhoneNumberChange}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.PhoneNumber")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.email")}
                          name={"email"}
                          value={item?.otherParentDetails?.email}
                          onChange={handleParentTwoEmailChange}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Email")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          required
                          label={t("person.parentshipType")}
                          name={"parentshipTypeId"}
                          value={item?.otherParentDetails?.parentshipTypeId}
                          onChange={handleParentTwoParentshipTypeChange}
                          selectData={codeBookToSelectData(parentshipTypes)}
                          errorText={errors?.find(e => e.propertyName === "OtherParentDetails.ParentshipTypeId")?.message}
                        />
                      </Col>
                    </Row>
                    {(item?.otherParentDetails?.parentshipTypeId === ParentshipTypeEnum.Custodian) &&
                      <Row>
                        <Col md={12}>
                          <TextField
                            required
                            type="text"
                            label={`${t("person.custodian")}`}
                            name={"custody"}
                            value={item?.otherParentDetails.custody}
                            onChange={handleParentTwoCustodyChange}
                            errorText={errors?.find(e => e.propertyName === "OtherParentDetails.Custody")?.message}
                          />
                        </Col>
                      </Row>
                    }
                  </FormCard>
                </FormCard>
              }
              {(item.step === RequestStep.ChildData) &&
                <FormCard
                  cardTitle={t("request.childStep")}
                >
                  <Row>
                    <Col md={12}>
                      <InfoPanel
                        message={t("request.mainInfoMessageChild")}
                        color="warning"
                        fullWidth
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={8}>
                    </Col>
                    <Col md={4}>
                      <Button
                        color="primary"
                        disabled={!(item.childDetails?.person?.oib !== undefined && oibRegex.test(item.childDetails.person.oib))}
                        outline
                        onClick={() => handleUpdateServiceData(PersonType.Child)}
                        size="sm"
                      >
                        {t("common.schoolCandidateModel.refreshOibData")}
                      </Button>
                    </Col>
                  </Row>
                  <FormCard
                    cardTitle={t("request.basicInformation")}
                  >
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={t("person.firstName")}
                          helperText={t("common.birthRegistry")}
                          value={item?.childDetails?.firstName}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={t("person.lastName")}
                          helperText={t("common.birthRegistry")}
                          value={item?.childDetails?.lastName}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.oib")}
                          helperText={t("common.oibApi")}
                          value={item?.childDetails?.person.oib}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.dateOfBirth")}
                          helperText={t("common.birthRegistry")}
                          value={item?.childDetails
                            ? hrDate(item?.childDetails.dateOfBirth)
                            : ""
                          }
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.sex")}
                          helperText={t("common.birthRegistry")}
                          value={genders?.find(v => v.id === item?.childDetails?.genderId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.birthplace")}
                          helperText={t("common.birthRegistry")}
                          value={item?.childDetails?.cityOfBirth}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={3}>
                        <TextField
                          type="text"
                          label={t("person.countryBirth")}
                          helperText={t("common.birthRegistry")}
                          value={countries?.find(v => v.id === item?.childDetails?.countryOfBirthId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.citizenship")}
                          helperText={t("common.birthRegistry")}
                          value={citizenships?.find(v => v.id === item?.childDetails?.citizenshipId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          emptyOption={{ value: undefined, text: "" }}
                          label={t("person.nationalMinority")}
                          name={"nationalMinority"}
                          value={item?.childDetails?.nationalMinorityId}
                          onChange={handleChildNationalMinorityChange}
                          selectData={codeBookToSelectData(nationalMinorities)}
                          errorText={errors?.find(e => e.propertyName === "ChildDetails.NationalMinorityId")?.message}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          label={t("person.nationality")}
                          name={"nationality"}
                          value={item?.childDetails?.nationalityId}
                          onChange={handleChildNationalityChange}
                          selectData={codeBookToSelectData(nationalities)}
                          errorText={errors?.find(e => e.propertyName === "ChildDetails.NationalityId")?.message}
                        />
                      </Col>
                    </Row>
                  </FormCard>
                  <FormCard
                    cardTitle={t("person.permanentResidenceInformation")}
                  >
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.country")}`}
                          value={countries?.find(v => v.id === item?.childDetails?.residence?.countryId)?.name}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.municipality"
                          )}`}
                          value={item?.childDetails?.residence?.permanentAddress?.municipality ?? item?.childDetails?.residence?.address}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.town")}`}
                          value={item?.childDetails?.residence?.permanentAddress?.town}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.postalCode"
                          )}`}
                          value={item?.childDetails?.residence?.permanentAddress?.postalCode}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t("person.street")}`}
                          value={item?.childDetails?.residence?.permanentAddress?.street}
                          plaintext
                          bold
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          label={`${t("person.permanentResidence")} - ${t(
                            "person.streetNumber"
                          )}`}
                          value={item?.childDetails?.residence?.permanentAddress?.houseNumber}
                          plaintext
                          bold
                        />
                      </Col>
                    </Row>
                  </FormCard>
                  {item?.childDetails?.residence?.temporaryAddress && (
                    <FormCard
                      cardTitle={t("person.temporaryResidenceInformation")}
                    >
                      <Row>
                        <Col md={4}>
                          {/*<TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.country"
                            )}`}
                            value={countries?.find(v => v.id === item?.childDetails?.residence?.countryId)?.name}
                            plaintext
                            bold
                            />*/}
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.municipality"
                            )}`}
                            value={item?.childDetails?.residence?.temporaryAddress?.municipality}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.town"
                            )}`}
                            value={item?.childDetails?.residence?.temporaryAddress?.town}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.postalCode"
                            )}`}
                            value={item?.childDetails?.residence?.temporaryAddress?.postalCode}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.street"
                            )}`}
                            value={item?.childDetails?.residence?.temporaryAddress?.street}
                            plaintext
                            bold
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumber"
                            )}`}
                            value={item?.childDetails?.residence?.temporaryAddress?.houseNumber}
                            plaintext
                            bold
                          />
                        </Col>
                      </Row>
                    </FormCard>
                  )}
                </FormCard>
              }
              {(item.step === RequestStep.InstitutionProgramData) &&
                <InstitutionData
                  item={item}
                  primaryInstitution={primaryInstitution}
                  handleUpdate={handleUpdate}
                  errors={errors}
                />
              }
              {(item.step === RequestStep.InitialQuestionnaireData) &&
                <FormCard
                  cardTitle={t("request.initialConversationStep")}
                >
                  {getQuestionnaireDisplay()}
                </FormCard>
              }
              {(item.step === RequestStep.DocumentationData) &&
                <FormCard
                  cardTitle={t("request.documentationStep")}
                >
                  <Row>
                    <Col md={12}>
                      <InfoPanel
                        title={t("request.mainInfoTitleDocumentation")}
                        message={t("request.mainInfoMessageDocumentation")}
                        color="warning"
                        fullWidth
                      />
                    </Col>
                  </Row>
                  {/* With the new functionality to change a RequestType, we 
                  need to change the display logic. If there exists a document type 
                  which does not match the RequestType, we will display it without 
                  allowing changes.
                  */}
                  {(item.type === RequestType.EarlyEnrollment
                    || (item.psychophysicalConditionDecisionAttachments?.some(v => true) ?? false)) &&
                    <>
                      <FormCard cardTitle={t("request.documentPsychophysicalConditionDecision")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.psychophysicalConditionDecisionAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.PsychophysicalConditionDecision)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.PsychophysicalConditionDecision)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.EarlyEnrollment) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handlePsychophysicalConditionDecisionUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.psychophysicalConditionDecisionUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                    </>
                  }
                  {(item.type === RequestType.TemporaryExemption
                    || (item.uniqueBodyOfExpertiseAttachments?.some(v => true) ?? false)) &&
                    <>
                      <FormCard cardTitle={t("request.documentUniqueBodyOfExpertise")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.uniqueBodyOfExpertiseAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.UniqueBodyOfExpertise)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.UniqueBodyOfExpertise)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.TemporaryExemption) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleUniqueBodyOfExpertiseUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.uniqueBodyOfExpertiseUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                    </>
                  }
                  {(!(item.type === RequestType.TemporaryExemption
                    || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties)
                    || (item.childAttachments?.some(v => true) ?? false)) &&
                    <>
                      <FormCard cardTitle={t("request.documentChild")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.childAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.Child)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.Child)}
                              removable={true}
                            />
                          </Col>
                          {!(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleChildUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.childUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                    </>
                    }
                    {(!(item.type === RequestType.TemporaryExemption
                      || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties)
                      || (item.parentAttachments?.some(v => true) ?? false)) &&
                    <>
                      <FormCard cardTitle={t("request.documentParent")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.parentAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.Parent)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.Parent)}
                              removable={true}
                            />
                          </Col>
                          {!(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleParentUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.parentUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                    </>
                  }
                  {(item.type === RequestType.TemporaryExemption
                    || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                    || (item.medicalAttachments?.some(v => true) ?? false)) &&
                    <>
                      <FormCard cardTitle={t("request.documentMedical")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.medicalAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.Medical)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.Medical)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleMedicalUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.medicalUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                      </>
                    }
                    {(item.type === RequestType.TemporaryExemption
                      || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                      || (item.psychologyAttachments?.some(v => true) ?? false)) &&
                      <>
                      <FormCard cardTitle={t("request.documentPsychology")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.psychologyAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.Psychology)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.Psychology)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handlePsychologyUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.psychologyUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                      </>
                    }
                    {(item.type === RequestType.TemporaryExemption
                      || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                      || (item.educationalRehabilitationAttachments?.some(v => true) ?? false)) &&
                      <>
                      <FormCard cardTitle={t("request.documentEducation")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.educationalRehabilitationAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.EducationalRehabilitation)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.EducationalRehabilitation)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleEducationUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.educationalRehabilitationUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                      </>
                    }
                    {(item.type === RequestType.TemporaryExemption
                      || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties
                      || (item.otherAttachments?.some(v => true) ?? false)) &&
                      <>
                      <FormCard cardTitle={t("request.documentOther")}>
                        <Row>
                          <Col md={4}>
                            <DocumentList
                              documents={item.otherAttachments ?? []}
                              onDocumentClick={(doc) => handleDocumentDownload(doc, DocumentType.OtherParent)}
                              onRemoveClick={(doc) => handleRemoveAttachment(doc, DocumentType.OtherParent)}
                              removable={true}
                            />
                          </Col>
                          {(item.type === RequestType.TemporaryExemption
                            || item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties) &&
                          <>
                          <Col md={4}>
                            <Button
                              color="primary"
                              onClick={handleOtherUploadDocumentClick}
                              size="sm"
                            >
                              {t("request.documentUpload")}
                            </Button>
                          </Col>
                          <Col md={4}>
                            <DocumentList
                              documents={item.otherUploadFiles ?? []}
                              removable={false}
                            />
                          </Col>
                          </>
                          }
                        </Row>
                      </FormCard>
                    </>
                  }
                </FormCard>
              }
              {(item.step === RequestStep.Approval) &&
                <FormCard
                  cardTitle={t("request.confirmStep")}
                >
                  <FormCard>
                    <Row>
                      <Col md={6}>
                        {t("request.parentsStep")}
                      </Col>
                      <Col md={6}>
                        <Button
                          size="sm"
                          outline
                          onClick={(e) => handleStepChange(RequestStep.ParentData)}
                        >
                          {t("common.edit")}
                        </Button>
                      </Col>
                    </Row>
                  </FormCard>
                  <FormCard>
                    <Row>
                      <Col md={6}>
                        {t("request.childStep")}
                      </Col>
                      <Col md={6}>
                        <Button
                          size="sm"
                          outline
                          onClick={(e) => handleStepChange(RequestStep.ChildData)}
                        >
                          {t("common.edit")}
                        </Button>
                      </Col>
                    </Row>
                  </FormCard>
                  {((item.type === RequestType.RegularEnrollment) || (item.type === RequestType.RegularEnrollmentForChildrenWithLearningDifficulties)) &&
                    <>
                      <FormCard>
                        <Row >
                          <Col md={6}>
                            {t("request.institutionStep")}
                          </Col>
                          <Col md={6}>
                            <Button
                              size="sm"
                              outline
                              onClick={(e) => handleStepChange(RequestStep.InstitutionProgramData)}
                            >
                              {t("common.edit")}
                            </Button>
                          </Col>
                        </Row>
                      </FormCard>
                      <FormCard>
                        <Row >
                          <Col md={6}>
                            {t("request.initialConversationStep")}
                          </Col>
                          <Col md={3}>
                            <Button
                              size="sm"
                              outline
                              onClick={(e) => handleStepChange(RequestStep.InitialQuestionnaireData)}
                            >
                              {t("common.edit")}
                            </Button>
                          </Col>
                          <Col md={3}>
                            <Button
                              size="sm"
                              outline
                              onClick={handleGetQuestionnaireClicked}
                            >
                              {t("request.getQuestionnaire")}
                            </Button>
                          </Col>
                        </Row>
                      </FormCard>
                    </>
                  }
                  <FormCard>
                    <Row>
                      <Col md={6}>
                        {t("request.documentationStep")}
                      </Col>
                      <Col md={6}>
                        <Button
                          size="sm"
                          outline
                          onClick={(e) => handleStepChange(RequestStep.DocumentationData)}
                        >
                          {t("common.edit")}
                        </Button>
                      </Col>
                    </Row>
                  </FormCard>
                </FormCard>
              }
              <DocumentsModal
                title={t("accessibility.documentModal.title")}
                infoPanelText={t("accessibility.documentModal.info")}
                types={t("accessibility.documentModal.types")}
                allDocuments={uploadDocument ?? []}
                maxSize={10}
                filesAction={handleFileChange}
                open={showUploadModal !== undefined}
                onConfirm={handleUploadFileChange}
                onClose={() => { setUploadDocument([]); setShowUploadModal(undefined); }}
                hideOldDocuments={false}
                btnCancelText={t("common.quit")}
                btnOkText={t("common.addNew")}
              />
              <ToolbarStepper
                titlePrev={t("request.prevStepButton")}
                titleNext={item.step !== RequestStep.Approval ? t("request.nextStepButton") : t("request.sendRequestButton")}
                disablePrev={item.step === RequestStep.ParentData && (item.subStep ?? 0) === 0}
                disableNext={false}
                onClickPrev={() => handleSaveClicked(RequestStepType.PreviousStep)}
                onClickNext={() => handleSaveClicked(RequestStepType.NextStep)}
              />
              <ToolbarEdit
                showCancel={"show"}
                showSave={"show"}
                onClickSave={() => handleSaveClicked(RequestStepType.SaveOnly)}
                showCustom={true}
              >
                <Button
                  outline
                  size="sm"
                  color="danger"
                  onClick={handleDeleteClicked}
                >
                  {(item.type === RequestType.EarlyEnrollment) || (item.type === RequestType.TemporaryExemption) ? t("common.cancel2") : t("common.delete")}
                </Button>
              </ToolbarEdit>
            </ContentArea>
          </Col>
        </Row>
      </StepperProvider>
      <Modal
        type={"message"}
        confirmText={t("common.close")}
        open={actionMessage !== undefined}
        onClose={() => { setActionMessage(undefined); handleRedirect(); }}
        onConfirm={() => { setActionMessage(undefined); handleRedirect(); }}
      >
        {actionMessage}
      </Modal>
      <Modal
        type={"confirm"}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        open={confirmMessage !== undefined}
        onConfirm={handleConfirmClicked}
        onClose={() => setConfirmMessage(undefined)}
      >
        {confirmMessage}
      </Modal>
    </>
  );
}  